<template>
	<div
		class="material-card"
		:class="[
			`material-card--${type}`,
			{ 'material-card--hidden': isHidden },
			{ 'material-card--draft': isDraft },
		]"
	>
		<!-- Меню действий (три точки) -->
		<div class="material-card__menu">
			<button
				type="button"
				class="material-card__menu-button"
				ref="menuButton"
				@click="toggleMenu"
				aria-label="Меню действий"
			>
				⋮
			</button>
		</div>

		<!-- Метка черновика (если нужно) -->
		<div v-if="isDraft" class="material-card__draft-badge">Черновик</div>

		<!-- Метка скрытого материала (для запланированной публикации) -->
		<div v-if="!isDraft && isHidden" class="material-card__hidden-badge">Скрыто</div>

		<!-- Бейдж типа (НЕ показываем если карточка скрыта или черновик) -->
		<div
			v-if="!isDraft && !isHidden"
			class="material-card__type-badge"
		>
			<span v-if="type === 'lessons' && lessonType">{{ lessonTypeLabel }}</span>
			<span v-else-if="type === 'tests'">Тест</span>
			<span v-else-if="type === 'practice'">Практика</span>
			<span v-else-if="type === 'homework'">Домашнее задание</span>
		</div>

		<div class="material-card__content">
			<h3 class="material-card__title">{{ title || 'Без названия' }}</h3>

			<!-- ===== УРОКИ ===== -->
			<div v-if="type === 'lessons'" class="material-card__info">
				<p v-if="scheduledPublishAt && !isDraft" class="material-card__info-item">
					<span class="material-card__info-label">Публикация:</span>
					{{ formattedPublishDate }}
				</p>
			</div>

			<!-- ===== ТЕСТЫ ===== -->
			<div v-if="type === 'tests'" class="material-card__info">
				<p v-if="autoPublish" class="material-card__info-item">
					<span class="material-card__info-label">Автопубликация:</span>
					{{ autoPublish }}
				</p>
				<p v-if="passingScore" class="material-card__info-item">
					<span class="material-card__info-label">Проходной балл:</span>
					{{ passingScore }}
				</p>
				<div v-if="questionsCount" class="material-card__info-row">
					<p class="material-card__info-item">
						<span class="material-card__info-label">Вопросов:</span>
						{{ questionsCount }}
					</p>
					<button
						type="button"
						class="material-card__visibility-btn"
						:class="{ 'material-card__visibility-btn--hidden': isHidden }"
						@click.stop="handleToggleVisibility"
						:title="isHidden ? 'Показать' : 'Скрыть'"
					>
						<span class="material-card__visibility-icon" aria-hidden="true">
							<svg
								v-if="isHidden"
								viewBox="0 0 24 24"
								fill="none"
								xmlns="http://www.w3.org/2000/svg"
							>
								<path
									d="M1.5 12C3.5 7.8 7.3 5 12 5C16.7 5 20.5 7.8 22.5 12C20.5 16.2 16.7 19 12 19C7.3 19 3.5 16.2 1.5 12Z"
									stroke="currentColor"
									stroke-width="1.8"
									stroke-linecap="round"
									stroke-linejoin="round"
								/>
								<circle
									cx="12"
									cy="12"
									r="3"
									stroke="currentColor"
									stroke-width="1.8"
								/>
							</svg>
							<svg
								v-else
								viewBox="0 0 24 24"
								fill="none"
								xmlns="http://www.w3.org/2000/svg"
							>
								<path
									d="M3 4L21 20"
									stroke="currentColor"
									stroke-width="1.8"
									stroke-linecap="round"
								/>
								<path
									d="M10.6 6.2C11 6.1 11.5 6 12 6C16.7 6 20.5 8.8 22.5 13C21.9 14.3 21.1 15.4 20.1 16.4"
									stroke="currentColor"
									stroke-width="1.8"
									stroke-linecap="round"
									stroke-linejoin="round"
								/>
								<path
									d="M17 18.4C15.5 19.1 13.8 19.5 12 19.5C7.3 19.5 3.5 16.7 1.5 12.5C2.3 10.8 3.5 9.3 5 8.2"
									stroke="currentColor"
									stroke-width="1.8"
									stroke-linecap="round"
									stroke-linejoin="round"
								/>
								<path
									d="M9.9 10.1C9.4 10.6 9 11.3 9 12C9 13.7 10.3 15 12 15C12.7 15 13.4 14.8 13.9 14.4"
									stroke="currentColor"
									stroke-width="1.8"
									stroke-linecap="round"
									stroke-linejoin="round"
								/>
							</svg>
						</span>
						<span class="material-card__visibility-text">{{ isHidden ? 'Показать' : 'Скрыть' }}</span>
					</button>
				</div>
				<p v-if="attempts" class="material-card__info-item">
					<span class="material-card__info-label">Попыток:</span>
					{{ attempts }}
				</p>
			</div>

			<!-- ===== ПРАКТИКА ===== -->
			<div v-if="type === 'practice'" class="material-card__info">
				<p v-if="dueDate" class="material-card__info-item">
					<span class="material-card__info-label">Срок сдачи:</span>
					{{ dueDate }}
				</p>
			</div>

			<!-- ===== ДЗ ===== -->
			<div v-if="type === 'homework'" class="material-card__info">
				<p v-if="dueDate" class="material-card__info-item">
					<span class="material-card__info-label">Срок сдачи:</span>
					{{ dueDate }}
				</p>
				<p v-if="dueTime" class="material-card__info-item">
					<span class="material-card__info-label">Время сдачи:</span>
					{{ dueTime }}
				</p>
			</div>
		</div>
	</div>

	<!-- ВЫПАДАЮЩЕЕ МЕНЮ (телепорт в body, чтобы не резалось) -->
	<teleport to="body">
		<transition name="dropdown-fade">
			<div
				v-if="isMenuOpen"
				class="material-card__dropdown"
				:style="dropdownStyles"
				ref="dropdown"
			>
				<button
					type="button"
					class="material-card__dropdown-item"
					@click="handleEdit"
				>
					<span class="material-card__dropdown-icon" aria-hidden="true">
						<svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
							<path
								d="M4 20H8L18.5 9.5C19.3 8.7 19.3 7.3 18.5 6.5C17.7 5.7 16.3 5.7 15.5 6.5L5 17V20Z"
								stroke="currentColor"
								stroke-width="1.8"
								stroke-linecap="round"
								stroke-linejoin="round"
							/>
							<path d="M13.5 8.5L16.5 11.5" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" />
						</svg>
					</span>
					<span>Редактировать</span>
				</button>

				<button
					v-if="type === 'lessons'"
					type="button"
					class="material-card__dropdown-item"
					@mousedown.stop="handlePreviewIntent"
					@click="handlePreview"
				>
					<span class="material-card__dropdown-icon" aria-hidden="true">
						<svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
							<path
								d="M1.5 12C3.5 7.8 7.3 5 12 5C16.7 5 20.5 7.8 22.5 12C20.5 16.2 16.7 19 12 19C7.3 19 3.5 16.2 1.5 12Z"
								stroke="currentColor"
								stroke-width="1.8"
								stroke-linecap="round"
								stroke-linejoin="round"
							/>
							<circle
								cx="12"
								cy="12"
								r="3"
								stroke="currentColor"
								stroke-width="1.8"
							/>
						</svg>
					</span>
					<span>Предпросмотр урока</span>
				</button>

				<button
					v-if="type === 'practice'"
					type="button"
					class="material-card__dropdown-item"
					@mousedown.stop="handlePreviewIntent"
					@click="handlePreview"
				>
					<span class="material-card__dropdown-icon" aria-hidden="true">
						<svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
							<path d="M1.5 12C3.5 7.8 7.3 5 12 5C16.7 5 20.5 7.8 22.5 12C20.5 16.2 16.7 19 12 19C7.3 19 3.5 16.2 1.5 12Z" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/>
							<circle cx="12" cy="12" r="3" stroke="currentColor" stroke-width="1.8"/>
						</svg>
					</span>
					<span>Предпросмотр практики</span>
				</button>

				<button
					v-if="type === 'homework'"
					type="button"
					class="material-card__dropdown-item"
					@mousedown.stop="handlePreviewIntent"
					@click="handlePreview"
				>
					<span class="material-card__dropdown-icon" aria-hidden="true">
						<svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
							<path d="M1.5 12C3.5 7.8 7.3 5 12 5C16.7 5 20.5 7.8 22.5 12C20.5 16.2 16.7 19 12 19C7.3 19 3.5 16.2 1.5 12Z" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/>
							<circle cx="12" cy="12" r="3" stroke="currentColor" stroke-width="1.8"/>
						</svg>
					</span>
					<span>Предпросмотр ДЗ</span>
				</button>

				<button
					v-if="type === 'tests'"
					type="button"
					class="material-card__dropdown-item"
					@mousedown.stop="handlePreviewIntent"
					@click="handlePreview"
				>
					<span class="material-card__dropdown-icon" aria-hidden="true">
						<svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
							<path d="M1.5 12C3.5 7.8 7.3 5 12 5C16.7 5 20.5 7.8 22.5 12C20.5 16.2 16.7 19 12 19C7.3 19 3.5 16.2 1.5 12Z" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round"/>
							<circle cx="12" cy="12" r="3" stroke="currentColor" stroke-width="1.8"/>
						</svg>
					</span>
					<span>Предпросмотр теста</span>
				</button>

				<button
					type="button"
					class="material-card__dropdown-item"
					@click="handleToggleVisibility"
				>
					<span class="material-card__dropdown-icon" aria-hidden="true">
						<svg
							v-if="isHidden"
							viewBox="0 0 24 24"
							fill="none"
							xmlns="http://www.w3.org/2000/svg"
						>
							<path
								d="M1.5 12C3.5 7.8 7.3 5 12 5C16.7 5 20.5 7.8 22.5 12C20.5 16.2 16.7 19 12 19C7.3 19 3.5 16.2 1.5 12Z"
								stroke="currentColor"
								stroke-width="1.8"
								stroke-linecap="round"
								stroke-linejoin="round"
							/>
							<circle
								cx="12"
								cy="12"
								r="3"
								stroke="currentColor"
								stroke-width="1.8"
							/>
						</svg>
						<svg
							v-else
							viewBox="0 0 24 24"
							fill="none"
							xmlns="http://www.w3.org/2000/svg"
						>
							<path
								d="M3 4L21 20"
								stroke="currentColor"
								stroke-width="1.8"
								stroke-linecap="round"
							/>
							<path
								d="M10.6 6.2C11 6.1 11.5 6 12 6C16.7 6 20.5 8.8 22.5 13C21.9 14.3 21.1 15.4 20.1 16.4"
								stroke="currentColor"
								stroke-width="1.8"
								stroke-linecap="round"
								stroke-linejoin="round"
							/>
							<path
								d="M17 18.4C15.5 19.1 13.8 19.5 12 19.5C7.3 19.5 3.5 16.7 1.5 12.5C2.3 10.8 3.5 9.3 5 8.2"
								stroke="currentColor"
								stroke-width="1.8"
								stroke-linecap="round"
								stroke-linejoin="round"
							/>
							<path
								d="M9.9 10.1C9.4 10.6 9 11.3 9 12C9 13.7 10.3 15 12 15C12.7 15 13.4 14.8 13.9 14.4"
								stroke="currentColor"
								stroke-width="1.8"
								stroke-linecap="round"
								stroke-linejoin="round"
							/>
						</svg>
					</span>
					<span>{{ isHidden ? 'Показать' : 'Скрыть' }}</span>
				</button>

				<button
					type="button"
					class="material-card__dropdown-item material-card__dropdown-item--danger"
					@click="handleDelete"
				>
					<span class="material-card__dropdown-icon" aria-hidden="true">
						<svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
							<path
								d="M4 7H20"
								stroke="currentColor"
								stroke-width="1.8"
								stroke-linecap="round"
							/>
							<path
								d="M9 3H15"
								stroke="currentColor"
								stroke-width="1.8"
								stroke-linecap="round"
							/>
							<path
								d="M7 7L8 20H16L17 7"
								stroke="currentColor"
								stroke-width="1.8"
								stroke-linecap="round"
								stroke-linejoin="round"
							/>
							<path d="M10 11V17" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" />
							<path d="M14 11V17" stroke="currentColor" stroke-width="1.8" stroke-linecap="round" />
						</svg>
					</span>
					<span>Удалить</span>
				</button>
			</div>
		</transition>
	</teleport>

	<!-- МОДАЛКА УДАЛЕНИЯ (тоже в body) -->
	<teleport to="body">
		<transition name="modal-fade">
			<div
				v-if="showDeleteModal"
				class="delete-modal"
				@click.self="closeDeleteModal"
			>
				<div class="delete-modal__content">
					<h3 class="delete-modal__title">Подтверждение удаления</h3>
					<p class="delete-modal__text">
						Вы уверены, что хотите удалить этот материал? Это действие нельзя
						отменить.
					</p>
					<div class="delete-modal__actions">
						<button
							type="button"
							class="delete-modal__button delete-modal__button--cancel"
							@click="closeDeleteModal"
						>
							Отменить
						</button>
						<button
							type="button"
							class="delete-modal__button delete-modal__button--delete"
							@click="confirmDelete"
						>
							Удалить
						</button>
					</div>
				</div>
			</div>
		</transition>
	</teleport>
</template>

<script>
export default {
	name: 'MaterialCard',
	props: {
		// ВАЖНО: оставляем plural
		type: {
			type: String,
			required: true,
			validator: value =>
				['lessons', 'tests', 'practice', 'homework'].includes(value),
		},
		title: {
			type: String,
			default: '',
		},
		// Для тестов
		autoPublish: {
			type: String,
			default: '',
		},
		duration: {
			type: [String, Number],
			default: null,
		},
		questionsCount: {
			type: Number,
			default: 0,
		},
		passingScore: {
			type: [String, Number],
			default: '',
		},
		attempts: {
			type: [String, Number],
			default: '',
		},
		// Для уроков
		lessonType: {
			type: String,
			default: '',
		},
		// Для практики/ДЗ
		dueDate: {
			type: String,
			default: '',
		},
		dueTime: {
			type: String,
			default: '',
		},
		isDraft: {
			type: Boolean,
			default: false,
		},
		isHidden: {
			type: Boolean,
			default: false,
		},
		scheduledPublishAt: {
			type: String,
			default: null,
		},
		materialId: {
			type: [String, Number],
			required: true,
		},
	},
	emits: ['edit', 'preview', 'preview-intent', 'toggle-visibility', 'delete'],
	data() {
		return {
			isMenuOpen: false,
			showDeleteModal: false,
			dropdownStyles: {
				position: 'absolute',
				top: '0px',
				left: '0px',
			},
		}
	},
	methods: {
		toggleMenu() {
			if (this.isMenuOpen) {
				this.closeMenu()
				return
			}
			this.isMenuOpen = true
			this.$nextTick(() => {
				const btn = this.$refs.menuButton
				if (!btn) return

				const rect = btn.getBoundingClientRect()
				const dropdownEl = this.$refs.dropdown
				const measured = dropdownEl?.getBoundingClientRect?.()
				const dropdownWidth = Math.ceil(measured?.width || 200)
				const dropdownHeight = Math.ceil(measured?.height || 160)
				const padding = 8

				const viewportW = window.innerWidth
				const viewportH = window.innerHeight

				let top = rect.bottom + 6
				const bottomSpace = viewportH - rect.bottom
				const topSpace = rect.top

				// если снизу мало места — открываем вверх
				if (
					bottomSpace < dropdownHeight + padding &&
					topSpace > dropdownHeight + padding
				) {
					top = rect.top - dropdownHeight - 6
				}

				// Default: align to the left edge of the trigger.
				// If it doesn't fit, align the right edge of the menu to the trigger.
				let left = rect.left
				if (left + dropdownWidth + padding > viewportW) {
					left = rect.right - dropdownWidth
				}
				const minLeft = padding
				const maxLeft = Math.max(padding, viewportW - dropdownWidth - padding)
				left = Math.min(Math.max(left, minLeft), maxLeft)

				this.dropdownStyles = {
					position: 'fixed', // Изменить на fixed
					top: top + 'px', // Убрать window.scrollY
					left: left + 'px', // Убрать window.scrollX
					maxWidth: Math.max(120, viewportW - padding * 2) + 'px',
				}
			})
		},
		closeMenu() {
			this.isMenuOpen = false
		},
		handleEdit() {
			this.closeMenu()
			this.$emit('edit', {
				id: this.materialId,
				type: this.type,
			})
		},
		handlePreview() {
			this.closeMenu()
			this.$emit('preview', {
				id: this.materialId,
				type: this.type,
			})
		},
		handlePreviewIntent() {
			this.$emit('preview-intent')
		},
		handleToggleVisibility() {
			this.closeMenu()
			this.$emit('toggle-visibility', {
				id: this.materialId,
				isHidden: this.isHidden, // Убрать инверсию - родитель сам решит
			})
		},
		handleDelete() {
			this.closeMenu()
			this.showDeleteModal = true
		},
		closeDeleteModal() {
			this.showDeleteModal = false
		},
		confirmDelete() {
			this.$emit('delete', {
				id: this.materialId,
				type: this.type,
			})
			this.closeDeleteModal()
		},
		onGlobalClick(event) {
			if (!this.isMenuOpen) return

			const btn = this.$refs.menuButton
			const dropdown = this.$refs.dropdown

			if (btn && btn.contains(event.target)) return
			if (dropdown && dropdown.contains(event.target)) return

			this.closeMenu()
		},
		onScroll() {
			if (this.isMenuOpen) {
				this.closeMenu()
			}
		},
	},
	mounted() {
		document.addEventListener('click', this.onGlobalClick)
		window.addEventListener('scroll', this.onScroll, true)
	},
	beforeUnmount() {
		document.removeEventListener('click', this.onGlobalClick)
		window.removeEventListener('scroll', this.onScroll, true)
	},

	computed: {
		lessonTypeLabel() {
			if (!this.lessonType) return ''
			const map = {
				video: 'Видео-урок',
				article: 'Статья',
			}
			// если вдруг придёт другой тип — вернём как есть
			return map[this.lessonType] || this.lessonType
		},
		formattedPublishDate() {
			if (!this.scheduledPublishAt) return ''
			
			// Парсим дату в формате "YYYY-MM-DD HH:MM" или ISO
			let dateStr = this.scheduledPublishAt.trim()
			
			// Если формат "YYYY-MM-DD HH:MM" - добавляем секунды для корректного парсинга
			if (/^\d{4}-\d{2}-\d{2} \d{2}:\d{2}$/.test(dateStr)) {
				dateStr = dateStr + ':00'
			}
			
			const date = new Date(dateStr)
			
			// Проверка на валидность даты
			if (isNaN(date.getTime())) return this.scheduledPublishAt
			
			const day = String(date.getDate()).padStart(2, '0')
			const month = String(date.getMonth() + 1).padStart(2, '0')
			const year = date.getFullYear()
			const hours = String(date.getHours()).padStart(2, '0')
			const minutes = String(date.getMinutes()).padStart(2, '0')
			return `${day}.${month}.${year} ${hours}:${minutes}`
		},
	},
}
</script>

<style scoped>
.material-card {
	position: relative;
	background-color: #ffffff;
	border: 1px solid rgba(0, 0, 0, 0.1);
	border-radius: 12px;
	padding: 16px;
	width: 100%;
	max-width: 320px;
	min-height: 140px;
	box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
	transition: all 0.3s ease;
}

.material-card:hover {
	box-shadow: 0 4px 16px rgba(0, 0, 0, 0.1);
	transform: translateY(-2px);
}

.material-card--draft {
	background-color: #f5f5f5;
	border: 2px dashed #9e9e9e;
}

.material-card--hidden {
	opacity: 0.6;
}

/* Цветная полоска слева по типам (как подсветка вкладок, можешь убрать если не надо) */
.material-card--lessons {
	border-left: 4px solid #4caf50;
}
.material-card--tests {
	border-left: 4px solid #0095ec;
}
.material-card--practice {
	border-left: 4px solid #ff9800;
}
.material-card--homework {
	border-left: 4px solid #9c27b0;
}

/* Меню действий */
.material-card__menu {
	position: absolute;
	top: 12px;
	right: 12px;
	z-index: 10;
}

.material-card__menu-button {
	width: 32px;
	height: 32px;
	display: flex;
	align-items: center;
	justify-content: center;
	background-color: #f5f5f5;
	border: 1px solid #e0e0e0;
	border-radius: 6px;
	font-size: 20px;
	font-weight: 600;
	color: rgba(0, 0, 0, 0.6);
	cursor: pointer;
	transition: all 0.2s ease;
}

.material-card__menu-button:hover {
	background-color: #e0e0e0;
	color: rgba(0, 0, 0, 0.8);
}

/* Дропдаун */
.material-card__dropdown {
	position: absolute;
	background-color: #ffffff;
	border: 1px solid #e0e0e0;
	border-radius: 8px;
	box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
	min-width: 200px;
	overflow: hidden;
	z-index: 1000;
}

.material-card__dropdown-item {
	display: flex;
	align-items: center;
	gap: 10px;
	width: 100%;
	padding: 12px 16px;
	background-color: transparent;
	border: none;
	font-family: 'Poppins', Helvetica;
	font-size: 14px;
	font-weight: 500;
	color: rgba(0, 0, 0, 0.8);
	text-align: left;
	cursor: pointer;
	transition: background-color 0.15s ease;
}

.material-card__dropdown-item:hover {
	background-color: #f5f5f5;
}

.material-card__dropdown-item--danger {
	color: #f44336;
}

.material-card__dropdown-item--danger:hover {
	background-color: #ffebee;
}

.material-card__dropdown-icon {
	display: inline-flex;
	align-items: center;
	justify-content: center;
	width: 16px;
	height: 16px;
	flex-shrink: 0;
}

.material-card__dropdown-icon svg {
	width: 16px;
	height: 16px;
	display: block;
}

/* Анимация дропдауна */
.dropdown-fade-enter-active,
.dropdown-fade-leave-active {
	transition: opacity 0.15s ease, transform 0.15s ease;
	transform-origin: top right;
}
.dropdown-fade-enter-from,
.dropdown-fade-leave-to {
	opacity: 0;
	transform: scale(0.95);
}

/* Бейджи */
.material-card__draft-badge,
.material-card__hidden-badge,
.material-card__scheduled-badge,
.material-card__type-badge {
	position: absolute;
	top: 12px;
	left: 12px;
	padding: 4px 12px;
	border-radius: 12px;
	font-family: 'Poppins', Helvetica;
	font-size: 12px;
	font-weight: 600;
	text-transform: uppercase;
}

.material-card__draft-badge {
	background-color: #ff9800;
	color: #ffffff;
}

.material-card__hidden-badge {
	background-color: #9e9e9e;
	color: #ffffff;
}

.material-card__scheduled-badge {
	background-color: #2196f3;
	color: #ffffff;
	text-transform: none;
	font-size: 11px;
}

.material-card__type-badge {
	background-color: #0095ec;
	color: #ffffff;
	font-size: 11px;
	padding: 3px 10px;
	transition: top 0.2s ease;
}

/* Смещение, если есть другие бейджи */
.material-card__type-badge--offset {
	top: 44px;
}

/* Цвет бейджа по типу */
.material-card--lessons .material-card__type-badge {
	background-color: #4caf50;
}
.material-card--tests .material-card__type-badge {
	background-color: #0095ec;
}
.material-card--practice .material-card__type-badge {
	background-color: #ff9800;
}
.material-card--homework .material-card__type-badge {
	background-color: #9c27b0;
}

.material-card__content {
	margin-top: 25px;
}

.material-card__title {
	color: #000000a6;
	font-family: 'Poppins', Helvetica;
	font-size: 17px;
}

.material-card__info {
	display: flex;
	flex-direction: column;
	gap: 4px;
}

.material-card__info-row {
	display: flex;
	align-items: center;
	justify-content: space-between;
	gap: 8px;
}

.material-card__info-item {
	color: rgba(0, 0, 0, 0.7);
	font-family: 'Poppins', Helvetica;
	font-size: 14px;
	margin: 0;
}

.material-card__info-label {
	font-weight: 600;
	color: rgba(0, 0, 0, 0.8);
	margin-right: 4px;
}

/* Кнопка скрытия/показа */
.material-card__visibility-btn {
	display: flex;
	align-items: center;
	gap: 4px;
	padding: 4px 8px;
	background-color: #f5f5f5;
	border: 1px solid #e0e0e0;
	border-radius: 6px;
	font-family: 'Poppins', Helvetica;
	font-size: 12px;
	font-weight: 500;
	color: rgba(0, 0, 0, 0.7);
	cursor: pointer;
	transition: all 0.2s ease;
}

.material-card__visibility-btn:hover {
	background-color: #e0e0e0;
	color: rgba(0, 0, 0, 0.9);
}

.material-card__visibility-btn--hidden {
	background-color: #e3f2fd;
	border-color: #90caf9;
	color: #1976d2;
}

.material-card__visibility-btn--hidden:hover {
	background-color: #bbdefb;
}

.material-card__visibility-icon {
	display: inline-flex;
	align-items: center;
	justify-content: center;
	width: 14px;
	height: 14px;
	flex-shrink: 0;
}

.material-card__visibility-icon svg {
	width: 14px;
	height: 14px;
	display: block;
}

.material-card__visibility-text {
	font-size: 11px;
}

/* Модалка */
.delete-modal {
	position: fixed;
	top: 0;
	left: 0;
	right: 0;
	bottom: 0;
	background-color: rgba(0, 0, 0, 0.5);
	display: flex;
	align-items: center;
	justify-content: center;
	z-index: 1000;
}

/* Анимация модалки - только фон */
.modal-fade-enter-active,
.modal-fade-leave-active {
	transition: opacity 0.2s ease;
}
.modal-fade-enter-from,
.modal-fade-leave-to {
	opacity: 0;
}

/* Анимация содержимого модалки */
.modal-fade-enter-active .delete-modal__content {
	animation: modal-scale-in 0.2s ease;
}
.modal-fade-leave-active .delete-modal__content {
	animation: modal-scale-out 0.2s ease;
}

@keyframes modal-scale-in {
	from {
		transform: scale(0.9);
		opacity: 0;
	}
	to {
		transform: scale(1);
		opacity: 1;
	}
}

@keyframes modal-scale-out {
	from {
		transform: scale(1);
		opacity: 1;
	}
	to {
		transform: scale(0.9);
		opacity: 0;
	}
}

.delete-modal__content {
	background-color: #ffffff;
	border-radius: 12px;
	padding: 30px;
	max-width: 450px;
	width: 90%;
	box-shadow: 0 8px 32px rgba(0, 0, 0, 0.2);
}

.delete-modal__title {
	font-family: 'Poppins', Helvetica;
	font-size: 22px;
	font-weight: 700;
	color: #000000;
	margin: 0 0 15px 0;
}

.delete-modal__text {
	font-family: 'Poppins', Helvetica;
	font-size: 16px;
	color: rgba(0, 0, 0, 0.7);
	margin: 0 0 25px 0;
	line-height: 1.5;
}

.delete-modal__actions {
	display: flex;
	gap: 12px;
	justify-content: flex-end;
}

.delete-modal__button {
	padding: 12px 24px;
	border: none;
	border-radius: 8px;
	font-family: 'Poppins', Helvetica;
	font-size: 15px;
	font-weight: 600;
	cursor: pointer;
	transition: all 0.2s ease;
}

.delete-modal__button--cancel {
	background-color: #f5f5f5;
	color: rgba(0, 0, 0, 0.7);
}

.delete-modal__button--cancel:hover {
	background-color: #e0e0e0;
}

.delete-modal__button--delete {
	background-color: #f44336;
	color: #ffffff;
}

.delete-modal__button--delete:hover {
	background-color: #d32f2f;
	box-shadow: 0 4px 12px rgba(244, 67, 54, 0.3);
}

/* Адаптивность */
@media (max-width: 768px) {
	.material-card {
		max-width: 100%;
	}
}

/* Dropdown sizing tweaks for the requested ranges */
@media (min-width: 769px) and (max-width: 1366px) {
	.material-card__dropdown {
		min-width: 190px;
	}
}

@media (min-width: 1367px) and (max-width: 1600px) {
	.material-card__dropdown {
		min-width: 200px;
	}
}

@media (min-width: 1601px) and (max-width: 1800px) {
	.material-card__dropdown {
		min-width: 210px;
	}
}

</style>
